import { Amplify } from "aws-amplify";

function cognitoClientId(): string {
  return process.env.REACT_APP_COGNITO_CLIENT_ID || "";
}

function cognitoUserPoolId(): string {
  return process.env.REACT_APP_POOL_ID || "";
}

function cognitoDomain(): string {
  return process.env.REACT_APP_COGNITO_DOMAIN || "";
}

function oauthScopes(): string[] {
  return (process.env.REACT_APP_OAUTH_SCOPES || "openid")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

function getRedirectBaseUrl(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return process.env.REACT_APP_APP_URL || "http://localhost:8081";
}

export const AUTH_CALLBACK_PATH = "/auth/callback";

export function isOAuthConfigured(): boolean {
  return !!(cognitoDomain() && cognitoClientId() && cognitoUserPoolId());
}

/** True when Okta/social sign-in should be shown (only requires Cognito domain). */
export function isOAuthDomainConfigured(): boolean {
  return !!cognitoDomain();
}

export function getOktaIdpName(): string {
  return process.env.REACT_APP_OKTA_IDP_NAME || "Okta";
}

/**
 * When true and Okta is configured, password / login-code paths are hidden so
 * institutions that mandate SAML get an IdP-only login screen.
 */
export function isSsoRequired(): boolean {
  if (!isOAuthDomainConfigured()) return false;
  const raw = (process.env.REACT_APP_FORCE_SSO || "").trim().toLowerCase();
  return raw === "1" || raw === "true" || raw === "yes";
}

/** Email domains that must authenticate via the configured SAML IdP. */
export function getSsoEmailDomains(): string[] {
  return (process.env.REACT_APP_SSO_EMAIL_DOMAINS || "")
    .split(",")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
}

export function emailRequiresSso(email: string): boolean {
  if (!isOAuthDomainConfigured()) return false;
  if (isSsoRequired()) return true;
  const domains = getSsoEmailDomains();
  if (domains.length === 0) return false;
  const normalized = email.trim().toLowerCase();
  const at = normalized.lastIndexOf("@");
  if (at < 0) return false;
  return domains.includes(normalized.slice(at + 1));
}

/**
 * When true, admin profiles must complete Cognito TOTP MFA before using the app.
 * Default off so existing prod admins are not locked out until rollout.
 */
export function isAdminMfaRequired(): boolean {
  const raw = (process.env.REACT_APP_REQUIRE_ADMIN_MFA || "")
    .trim()
    .toLowerCase();
  return raw === "1" || raw === "true" || raw === "yes";
}

export function configureAmplifyAuth(): void {
  const baseUrl = getRedirectBaseUrl();
  const redirectUri = `${baseUrl}${AUTH_CALLBACK_PATH}`;
  const domain = cognitoDomain();
  const scopes = oauthScopes();

  const cognitoConfig = {
    userPoolClientId: cognitoClientId(),
    userPoolId: cognitoUserPoolId(),
    ...(domain && {
      loginWith: {
        oauth: {
          domain: domain.replace(/^https?:\/\//, ""),
          scopes: scopes.length > 0 ? scopes : ["openid"],
          redirectSignIn: [redirectUri],
          redirectSignOut: [baseUrl],
          responseType: "code" as const,
        },
      },
    }),
  };

  Amplify.configure({
    Auth: {
      Cognito: cognitoConfig,
    },
  });
}
